import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { firstValueFrom } from 'rxjs';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ApiSystemSettingsService } from '../../../../core/services/api/system-settings/api-system-settings.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { ModalAdminActivityHistoryComponent } from '../../modals/system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../core/models/system-settings/admin-activity-log.model';
import {
  LINE_GUIDE_ACTION_OPTIONS,
  LINE_GUIDE_MAX_IMAGE_BYTES,
  LINE_GUIDE_REPLY_OPTIONS,
  LINE_GUIDE_STYLE_OPTIONS,
  LineGuideContent,
  LineGuideItem,
  LineGuideMenu,
  createEmptyLineGuideContent,
  createEmptyLineGuideItem,
  createEmptyLineGuideMenu,
  mapLineGuideConfigResponse,
  resolveLineGuideImageUrl,
  toLineGuideSavePayload,
} from '../../../../core/models/system-settings/line-guide-menu.model';

@Component({
  selector: 'app-line-guide-menu',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalAdminActivityHistoryComponent],
  templateUrl: './line-guide-menu.component.html',
  styleUrl: './line-guide-menu.component.css',
})
export class LineGuideMenuComponent implements OnInit {
  private readonly api = inject(ApiSystemSettingsService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  readonly actionOptions = LINE_GUIDE_ACTION_OPTIONS;
  readonly styleOptions = LINE_GUIDE_STYLE_OPTIONS;
  readonly replyOptions = LINE_GUIDE_REPLY_OPTIONS;
  readonly activityEntityTypes = AdminActivityEntityTypes;
  readonly maxImageBytes = LINE_GUIDE_MAX_IMAGE_BYTES;

  menu = signal<LineGuideMenu>(createEmptyLineGuideMenu());
  contents = signal<LineGuideContent[]>([]);
  isLoading = signal(false);
  isSaving = signal(false);
  lastFetchedTime = signal<Date | null>(null);
  showActivityHistory = false;
  showImageModal = false;
  selectedImageUrl = '';

  /** คู่มือที่เลือกได้ในปุ่มเมนู */
  guidePickerOptions = computed(() =>
    this.contents()
      .filter((c) => c.isActive && c.matchKey?.trim() && c.matchText?.trim())
      .map((c) => ({
        matchKey: c.matchKey.trim(),
        matchText: c.matchText.trim(),
        label: `${c.matchKey} · ${c.matchText}`,
      })),
  );

  trackByItemUiKey = (_: number, item: LineGuideItem) => item.uiKey ?? item.id;
  trackByContentUiKey = (_: number, c: LineGuideContent) => c.uiKey ?? c.id;

  ngOnInit(): void {
    this.load();
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get canShowActivityHistory(): boolean {
    return (this.menu()?.id ?? 0) > 0;
  }

  contentImageSrc(c: LineGuideContent): string {
    if (c.pendingBase64) return c.pendingBase64;
    return resolveLineGuideImageUrl(c.imageUrl);
  }

  load(): void {
    this.isLoading.set(true);
    this.api.getLineGuideConfig().subscribe({
      next: (res) => {
        const mapped = mapLineGuideConfigResponse(res);
        this.menu.set(mapped.menu);
        this.contents.set(mapped.contents);
        this.lastFetchedTime.set(new Date());
        this.isLoading.set(false);
      },
      error: () => {
        this.toast.error('โหลดคู่มือ LINE ล้มเหลว');
        this.isLoading.set(false);
      },
    });
  }

  async onSave(): Promise<void> {
    if (!this.canUpdate) {
      return;
    }

    const auth = this.resolveAuth();
    if (!auth) return;

    const menu = this.menu();
    let contents = this.contents();
    if (!menu.triggerText?.trim() || !menu.titleTh?.trim()) {
      this.toast.error('กรุณากรอกข้อความเปิดเมนูและหัวข้อ');
      return;
    }
    if (!menu.items?.length) {
      this.toast.error('กรุณาเพิ่มอย่างน้อย 1 ปุ่ม');
      return;
    }

    this.isSaving.set(true);
    try {
      contents = await this.uploadPendingImages(contents, auth);
      this.contents.set(contents);

      const res = await firstValueFrom(
        this.api.saveLineGuideConfig(toLineGuideSavePayload(menu, contents, auth.userId, auth.menuId)),
      );
      const ok = res?.success ?? res?.Success ?? res?.isSuccess ?? true;
      if (ok === false) {
        this.toast.error(res?.messageTh ?? res?.MessageTh ?? res?.message ?? 'บันทึกไม่สำเร็จ');
        return;
      }
      this.toast.success(res?.messageTh ?? res?.MessageTh ?? res?.message ?? 'บันทึกเรียบร้อย');
      this.load();
    } catch (err) {
      console.error('saveLineGuideConfig failed', err);
      this.toast.error(this.describeSaveError(err));
    } finally {
      this.isSaving.set(false);
    }
  }

  private async uploadPendingImages(
    contents: LineGuideContent[],
    auth: { userId: number; menuId: number },
  ): Promise<LineGuideContent[]> {
    const next = contents.map((c) => ({ ...c }));
    for (let i = 0; i < next.length; i++) {
      const row = next[i];
      if (!row.pendingBase64 || row.replyType !== 'image') continue;

      const res = await firstValueFrom(
        this.api.uploadLineGuideImage({
          userId: auth.userId,
          menuId: auth.menuId,
          fileName: row.pendingFileName || undefined,
          base64: row.pendingBase64,
        }),
      );
      const ok = res?.isSuccess ?? res?.IsSuccess ?? false;
      const url = res?.imageUrl ?? res?.ImageUrl ?? '';
      if (!ok || !url) {
        throw new Error(res?.message ?? res?.Message ?? `อัปโหลดรูปคู่มือ ${row.matchKey || i + 1} ไม่สำเร็จ`);
      }
      next[i] = {
        ...row,
        imageUrl: url,
        pendingBase64: null,
        pendingFileName: null,
      };
    }
    return next;
  }

  patchMenu(partial: Partial<LineGuideMenu>): void {
    this.menu.update((m) => ({ ...m, ...partial }));
  }

  addItem(): void {
    if (!this.canUpdate) return;
    this.menu.update((m) => {
      const nextSort = m.items.length ? Math.max(...m.items.map((i) => i.sortOrder)) + 10 : 10;
      return { ...m, items: [...m.items, createEmptyLineGuideItem(nextSort)] };
    });
  }

  removeItem(index: number): void {
    if (!this.canUpdate) return;
    this.menu.update((m) => ({
      ...m,
      items: m.items.filter((_, i) => i !== index),
    }));
  }

  moveItem(index: number, direction: -1 | 1): void {
    if (!this.canUpdate) return;
    this.menu.update((m) => {
      const items = [...m.items];
      const target = index + direction;
      if (target < 0 || target >= items.length) return m;
      [items[index], items[target]] = [items[target], items[index]];
      items.forEach((item, i) => (item.sortOrder = (i + 1) * 10));
      return { ...m, items };
    });
  }

  patchItem(index: number, partial: Partial<LineGuideItem>): void {
    this.menu.update((m) => {
      const item = m.items[index];
      if (item) Object.assign(item, partial);
      return { ...m, items: m.items };
    });
  }

  /** ปุ่มเมนูเลือกคู่มือ → ตั้ง guideKey + actionValue(matchText) */
  onPickGuide(itemIndex: number, matchKey: string): void {
    const guide = this.contents().find((c) => c.matchKey === matchKey);
    if (!guide) {
      this.patchItem(itemIndex, { guideKey: '', actionValue: '' });
      return;
    }
    this.patchItem(itemIndex, {
      guideKey: guide.matchKey,
      actionValue: guide.matchText,
      actionType: 'message',
    });
  }

  addContent(): void {
    if (!this.canUpdate) return;
    this.contents.update((list) => [...list, createEmptyLineGuideContent()]);
  }

  removeContent(index: number): void {
    if (!this.canUpdate) return;
    this.contents.update((list) => list.filter((_, i) => i !== index));
  }

  patchContent(index: number, partial: Partial<LineGuideContent>): void {
    this.contents.update((list) => {
      const row = list[index];
      if (row) Object.assign(row, partial);
      return list.slice();
    });
  }

  /** แนบรอก่อน — อัปโหลดจริงตอนกดบันทึก */
  onImageSelected(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แนบรูป');
      input.value = '';
      return;
    }

    if (!file.type.startsWith('image/')) {
      this.toast.error('กรุณาเลือกไฟล์รูปภาพ');
      input.value = '';
      return;
    }

    if (file.size > this.maxImageBytes) {
      this.toast.error('ไฟล์ใหญ่เกิน 10MB — ลดขนาดก่อนแนบ');
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.patchContent(index, {
        replyType: 'image',
        pendingBase64: String(reader.result || ''),
        pendingFileName: file.name,
      });
      input.value = '';
      this.toast.success('แนบรูปแล้ว — กดบันทึกเพื่ออัปโหลดและบันทึกคู่มือ');
    };
    reader.readAsDataURL(file);
  }

  clearPendingOrImage(index: number): void {
    this.patchContent(index, {
      pendingBase64: null,
      pendingFileName: null,
      imageUrl: '',
    });
  }

  openImageModal(url: string): void {
    if (!url || url.includes('notfound')) return;
    this.selectedImageUrl = url;
    this.showImageModal = true;
  }

  closeImageModal(): void {
    this.showImageModal = false;
    this.selectedImageUrl = '';
  }

  openHistory(): void {
    this.showActivityHistory = true;
  }

  private resolveAuth(): { userId: number; menuId: number } | null {
    // UserId จริงถูก BindActorUserIdFilter ทับจาก JWT ที่ API — body เป็น advisory
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!menuId) {
      this.toast.error(
        'ไม่พบสิทธิ์เมนูนี้ — ตรวจว่า path /system-settings/line-guide-menu (path_id 126) ถูกผูก role แล้ว',
      );
      return null;
    }
    return { userId: 0, menuId };
  }

  /** upload / save failure → readable toast (HTTP status, API message, model-validation fields) */
  private describeSaveError(err: unknown): string {
    const fallback = 'บันทึกคู่มือ LINE ล้มเหลว';
    if (err instanceof HttpErrorResponse) {
      const path = (err.url ?? '').split('?')[0].split('/').slice(-1)[0] || 'API';
      if (err.status === 0) return `${fallback} — เชื่อมต่อ API ไม่ได้`;
      if (err.status === 404) return `${fallback} — ไม่พบ API ${path} (404) ตรวจว่า API ที่ใช้อยู่ deploy เวอร์ชันล่าสุดแล้ว`;
      if (err.status === 401 || err.status === 403) return `${fallback} — ไม่มีสิทธิ์ (${err.status})`;
      const body = err.error;
      if (typeof body === 'string' && body.trim()) return `${fallback} — ${body.trim()}`;
      const fieldErrors = body?.errors
        ? Object.entries(body.errors as Record<string, string[]>)
            .map(([field, msgs]) => `${field}: ${(msgs ?? []).join(', ')}`)
            .join(' | ')
        : '';
      if (fieldErrors) return `${fallback} — ${fieldErrors}`;
      return `${fallback} — ${path} HTTP ${err.status}`;
    }
    if (err instanceof Error && err.message) return err.message;
    return fallback;
  }
}
